import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/layout/Header';
import BookGrid from './components/books/BookGrid';
import PersonalDashboard from './components/dashboard/PersonalDashboard';
import AdminPanel from './components/admin/AdminPanel';
import AuthForm from './components/auth/AuthForm';
import FilterPanel from './components/filters/FilterPanel';
import { useBooks } from './hooks/useBooks';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import '@questlabs/react-sdk/dist/style.css';
import './index.css';

const { FiBook, FiAlertTriangle, FiRefreshCw, FiLogOut } = FiIcons;

const AppContent = () => {
  const { user, signOut, loading: authLoading } = useAuth();
  const [currentView, setCurrentView] = useState('home');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Initial state category changed from '' to []
  const [filters, setFilters] = useState({ category: [], available: false });
  const [showFilters, setShowFilters] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [showExitButton, setShowExitButton] = useState(false);

  const { books, loading: booksLoading, error: booksError, checkoutBook, returnBook, dynamicCategories } = useBooks(searchQuery, filters);

  useEffect(() => {
    let timer;
    if (authLoading) {
      timer = setTimeout(() => setShowExitButton(true), 3000);
    } else {
      setShowExitButton(false);
    }
    return () => clearTimeout(timer);
  }, [authLoading]);

  useEffect(() => {
    if (user && currentView === 'auth') {
      setCurrentView('home');
    }
  }, [user, currentView]);

  const handleCheckout = async (id) => {
    setCheckoutLoading(id);
    const res = await checkoutBook(id, user.id);
    setCheckoutLoading(null);
    return res;
  };

  const handleReturn = async (bookId, loanId) => {
    setCheckoutLoading(bookId);
    const res = await returnBook(bookId, loanId);
    setCheckoutLoading(null);
    return res;
  };

  const handleEmergencySignOut = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (err) {
      console.error("Emergency sign out failed", err);
      window.location.reload();
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-space_indigo flex flex-col items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <div className="bg-dark_cyan w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <SafeIcon icon={FiBook} className="w-8 h-8 text-white animate-pulse" />
          </div>
          <p className="text-white font-black uppercase tracking-[0.2em] text-xs mb-8">Opening the Library...</p>
          <AnimatePresence>
            {showExitButton && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <p className="text-white-300 text-[10px] mb-4">Taking longer than expected?</p>
                <button 
                  onClick={handleEmergencySignOut} 
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-xs font-black uppercase tracking-widest transition-all"
                >
                  Force Log Out
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    );
  }

  if (booksError && currentView === 'home') {
    return (
      <div className="min-h-screen bg-white-500 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="bg-hot_berry-900 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto border-4 border-hot_berry">
            <SafeIcon icon={FiAlertTriangle} className="w-10 h-10 text-hot_berry" />
          </div>
          <h2 className="text-2xl font-black text-space_indigo uppercase">Shelf Access Denied</h2>
          <p className="text-white-200 font-bold text-sm leading-relaxed">
            We couldn't load the book collection. This usually happens if the database is still initializing or your session has expired.
          </p>
          <div className="space-y-3">
            <button 
              onClick={() => window.location.reload()} 
              className="flex items-center justify-center w-full bg-space_indigo text-white py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-space_indigo-400 transition-all shadow-lg"
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" /> Try Again
            </button>
            <button 
              onClick={handleEmergencySignOut} 
              className="flex items-center justify-center w-full bg-white border-2 border-white-400 text-white-300 py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:text-hot_berry hover:border-hot_berry transition-all"
            >
              <SafeIcon icon={FiLogOut} className="w-4 h-4 mr-2" /> Emergency Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'auth' && !user) {
    return <AuthForm onBack={() => setCurrentView('home')} />;
  }

  const renderMainContent = () => {
    switch (currentView) {
      case 'dashboard':
        return user ? <PersonalDashboard /> : <AuthForm onBack={() => setCurrentView('home')} />;
      case 'admin':
        return user ? <AdminPanel /> : <AuthForm onBack={() => setCurrentView('home')} />;
      default:
        return (
          <>
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
              <div>
                <h2 className="text-3xl font-black text-space_indigo uppercase tracking-tight">
                  {searchQuery ? 'Search Results' : 'The Collection'}
                </h2>
                <p className="text-xs font-black text-white-300 uppercase tracking-widest mt-2">
                  {books.length} Volumes in Library
                </p>
              </div>
              <button onClick={() => setShowFilters(true)} className="bg-white border-2 border-white-400 px-6 py-3 rounded-xl hover:border-dark_cyan transition-all flex items-center text-xs font-black uppercase tracking-widest text-space_indigo">
                <SafeIcon icon={FiIcons.FiFilter} className="w-4 h-4 mr-3 text-dark_cyan" /> Refine Search
              </button>
            </div>
            <BookGrid 
              books={books} 
              loading={booksLoading} 
              onCheckout={handleCheckout} 
              onReturn={handleReturn} 
              checkoutLoading={checkoutLoading} 
              onLoginRedirect={() => setCurrentView('auth')} 
            />
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-white-500">
      <Header 
        searchQuery={searchQuery} 
        onSearch={setSearchQuery} 
        currentView={currentView} 
        setCurrentView={setCurrentView} 
      />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          <motion.div 
            key={currentView} 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -10 }}
          >
            {renderMainContent()}
          </motion.div>
        </AnimatePresence>
      </main>
      <AnimatePresence>
        {showFilters && (
          <FilterPanel 
            filters={filters} 
            onFilterChange={setFilters} 
            onClearFilters={() => setFilters({ category: [], available: false })} 
            isOpen={showFilters} 
            onToggle={() => setShowFilters(false)} 
            categories={dynamicCategories} 
          />
        )}
      </AnimatePresence>
      <Toaster position="bottom-right" />
    </div>
  );
};

const App = () => (
  <AuthProvider>
    <AppContent />
  </AuthProvider>
);

export default App;